import { makeAutoObservable, runInAction } from 'mobx';

import { axiosClient } from '../config/axios-client';
import { GameStage } from '../model/enumeration/GameStage.enum';
import {
  createEmptyEventsLog,
  LogEvent,
  LogsResponse,
} from '../model/LogsResponse';
import { StateStore } from './StateStore';

class EventLogsStore implements StateStore {
  private openedLogs: LogsResponse[] = [];
  private visibleLogs: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public resetState = (): void => {
    this.openedLogs = [];
    this.visibleLogs = [];
  };

  public getLogs = (tableId: string): Promise<LogsResponse> => {
    return new Promise(resolve => {
      const logResponse = this.openedLogs.find(log => log.tableId === tableId);
      if (logResponse) {
        resolve(logResponse);
      } else {
        this.loadLogs(tableId).then(response => resolve(response));
      }
    });
  };

  private loadLogs = (tableId: string): Promise<LogsResponse> =>
    axiosClient
      .get<LogsResponse>(`/tables/${tableId}/logs`)
      .then(response => response.data)
      .then(response => {
        const logs: LogsResponse = {
          tableId: response.tableId,
          events: {
            ...createEmptyEventsLog(),
            ...response.events,
          },
        };
        runInAction(() => {
          this.openedLogs.push(logs);
        });
        return logs;
      });

  public removeLogs = (tableId: string): void => {
    this.makeLogInvisible(tableId);
    this.openedLogs = this.openedLogs.filter(log => log.tableId !== tableId);
  };

  public addEventToLog = (
    tableId: string,
    event: LogEvent,
    stage: GameStage,
  ): void => {
    const eventLog = this.openedLogs.find(log => log.tableId === tableId);
    if (eventLog) {
      eventLog.events[stage].push(event);
    }
  };

  public isLogsVisible = (tableId: string): boolean => {
    return this.visibleLogs.includes(tableId);
  };

  public makeLogVisible = (tableId: string): void => {
    this.visibleLogs.push(tableId);
  };

  public makeLogInvisible = (tableId: string): void => {
    this.visibleLogs = this.visibleLogs.filter(id => id !== tableId);
  };

  public toggleLogVisibility = (tableId: string): void => {
    if (this.isLogsVisible(tableId)) {
      this.makeLogInvisible(tableId);
    } else {
      this.makeLogVisible(tableId);
    }
  };

  public emptyLogs = (tableId: string): void => {
    const logs = this.openedLogs.find(logs => logs.tableId === tableId);
    if (logs) {
      logs.events = {
        FLOP: [],
        BLINDS: [],
        TURN: [],
        PREFLOP: [],
        RIVER: [],
      };
    }
  };

  public addEmptyLogs = (tableId: string): void => {
    const logs: LogsResponse = {
      tableId: tableId,
      events: createEmptyEventsLog(),
    };
    this.openedLogs.push(logs);
  };

  public getAllOpenedLogs = (): LogsResponse[] => this.openedLogs;

  public setOpenedLogs = (logs: LogsResponse[]): void => {
    this.openedLogs = logs;
  };
}

const eventLogsStore = new EventLogsStore();
export default eventLogsStore;
